import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import ErrorBoundary from './components/ErrorBoundary';
import PortfolioOverview from './pages/PortfolioOverview';
import CommandCenter from './pages/CommandCenter';
import { UnifiedSettings } from './components/settings';
import Sidebar, { MobileHeader } from './components/Sidebar';
import { AuthProvider, useAuth } from './AuthContext';
import { ThemeProvider } from './ThemeContext';
import { TradingViewModalProvider } from './components/TradingViewModal';
import { TooltipProvider } from './components/ui/tooltip';
import Login from './pages/Login';
import DecisionLog from './pages/DecisionLog';
import BotInsightLog from './pages/BotInsightLog';
import TradeTransactionMonitor from './pages/TradeTransactionMonitor';
import UniverseMonitor from './pages/UniverseMonitor';
import IntelligenceHub from './pages/IntelligenceHub';
import AdminBugs from './pages/AdminBugs';
import BugReportWidget from './components/BugReportWidget';
import AskTitanPanel from './components/AskTitanPanel';
import MobileTradeDock from './components/MobileTradeDock';
import EquitiesRouteGuard from './components/EquitiesRouteGuard';
import { useEquitiesMode } from './hooks/useEquitiesMode';
import Admin from './pages/Admin';
import FleetDashboard from './pages/FleetDashboard';
import PlatformHealth from './pages/PlatformHealth';
import HermesDashboard from './pages/HermesDashboard';
import VerticalTradingLayout from './pages/trade/VerticalTradingLayout';
import VerticalOverviewTab from './pages/trade/VerticalOverviewTab';
import VerticalStrategyTab from './pages/trade/VerticalStrategyTab';
import VerticalConnectionsTab from './pages/trade/VerticalConnectionsTab';
import Reports from './pages/Reports';
import AiLearning from './pages/AiLearning';
import DayReview from './pages/DayReview';
import StrategyLabLayout from './pages/strategy-lab/StrategyLabLayout';
import WatchlistsPage from './pages/strategy-lab/WatchlistsPage';
import ScannersPage from './pages/strategy-lab/ScannersPage';
import StrategiesPage from './pages/strategy-lab/StrategiesPage';
import DeploymentsPage from './pages/strategy-lab/DeploymentsPage';
import ExecutionFocusBanner from './components/ExecutionFocusBanner';
import TradingStatusBar from './components/TradingStatusBar';
import { logout as localLogout } from './lib/localAuth';

/** Banner shown when an admin is impersonating another user */
const ImpersonationBanner = () => {
  const [info, setInfo] = React.useState<{ admin_email: string; target_email: string } | null>(null);
  const [returning, setReturning] = React.useState(false);

  React.useEffect(() => {
    const raw = localStorage.getItem('titan_impersonation');
    if (raw) {
      try { setInfo(JSON.parse(raw)); } catch { /* ignore */ }
    }
  }, []);

  if (!info) return null;

  const handleReturn = async () => {
    setReturning(true);
    localStorage.removeItem('titan_impersonation');
    // Clear the local session — admin will need to log back in
    localLogout();
    window.location.href = '/login';
  };

  return (
    <div className="bg-amber-500 text-amber-950 text-center py-1.5 px-4 text-sm font-medium flex items-center justify-center gap-3 z-[100]">
      <span>Viewing as <strong>{info.target_email}</strong></span>
      <button
        onClick={handleReturn}
        disabled={returning}
        className="px-2.5 py-0.5 bg-amber-950/20 hover:bg-amber-950/30 rounded text-xs font-semibold transition-colors"
      >
        {returning ? 'Returning...' : 'End Impersonation'}
      </button>
    </div>
  );
};

const ProtectedRoute = ({ component, roles }: { component: React.ReactElement; roles?: string[] }) => {
  const { currentUser } = useAuth();
  const [userRole, setUserRole] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchRole = async () => {
      if (currentUser) {
        try {
          const idTokenResult = await currentUser.getIdTokenResult();
          const role = (idTokenResult.claims.role as string) || 'Trader';
          setUserRole(role);
        } catch (error) {
          console.error('Error fetching role:', error);
          setUserRole('Trader');
        }
      }
      setLoading(false);
    };
    fetchRole();
  }, [currentUser]);

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (roles && roles.length > 0) {
    if (!userRole || !roles.includes(userRole)) {
      return (
        <div className="flex flex-col items-center justify-center h-full min-h-[400px] gap-3">
          <h2 className="text-xl font-semibold text-foreground">Access Denied</h2>
          <p className="text-muted-foreground">You don't have permission to access this page.</p>
          <p className="text-sm text-muted-foreground">Required role: <span className="font-medium text-foreground">{roles.join(' or ')}</span></p>
          <p className="text-sm text-muted-foreground">Your role: <span className="font-medium text-foreground">{userRole || 'None'}</span></p>
        </div>
      );
    }
  }

  return component;
};

/** Main app shell with responsive sidebar */
const AppShell = () => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const location = useLocation();
  const { equities_only: equitiesOnly } = useEquitiesMode();
  const tradeDockPad = location.pathname.startsWith('/trade/');

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <ImpersonationBanner />
      <div className="flex flex-1">
      <Sidebar mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0 md:py-3 md:pr-3">
        <MobileHeader onMenuClick={() => setMobileOpen(true)} />
        <main
          className={`flex-1 p-4 md:p-6 bg-card md:rounded-2xl overflow-auto scrollbar-thin md:border md:shadow-sm ${
            tradeDockPad ? 'pb-24 md:pb-6' : ''
          }`}
        >
          <EquitiesRouteGuard>
          <ExecutionFocusBanner />
          <TradingStatusBar />
          <Routes>
            {/* Main routes */}
            <Route path="/portfolio" element={<ProtectedRoute component={<PortfolioOverview />} roles={['Admin', 'Trader']} />} />
            <Route path="/fleet" element={<ProtectedRoute component={<FleetDashboard />} roles={['Admin', 'Trader']} />} />
            <Route path="/command-center" element={<ProtectedRoute component={<CommandCenter />} roles={['Admin', 'Trader']} />} />
            <Route
              path="/trade/:verticalId"
              element={<ProtectedRoute component={<VerticalTradingLayout />} roles={['Admin', 'Trader', 'Analyst']} />}
            >
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<VerticalOverviewTab />} />
              <Route path="performance" element={<Navigate to="/reports" replace />} />
              <Route path="strategy" element={<VerticalStrategyTab />} />
              <Route path="connections" element={<VerticalConnectionsTab />} />
            </Route>
            <Route
              path="/strategy-lab"
              element={<ProtectedRoute component={<StrategyLabLayout />} roles={['Admin', 'Trader']} />}
            >
              <Route index element={<Navigate to="watchlists" replace />} />
              <Route path="watchlists" element={<WatchlistsPage />} />
              <Route path="scanners" element={<ScannersPage />} />
              <Route path="strategies" element={<StrategiesPage />} />
              <Route path="deployments" element={<DeploymentsPage />} />
            </Route>
            <Route path="/autonomy" element={<Navigate to="/trade/stocks/overview" replace />} />
            <Route path="/signals" element={<ProtectedRoute component={<IntelligenceHub />} roles={['Admin', 'Trader', 'Analyst']} />} />
            <Route path="/intelligence" element={<Navigate to="/signals" replace />} />
            <Route path="/war-room/audit" element={<Navigate to="/signals?tab=audit" replace />} />
            <Route path="/reports" element={<ProtectedRoute component={<Reports />} roles={['Admin', 'Trader', 'Analyst']} />} />
            <Route path="/ai-learning" element={<ProtectedRoute component={<AiLearning />} roles={['Admin', 'Trader', 'Analyst']} />} />
            <Route path="/day-review" element={<ProtectedRoute component={<DayReview />} roles={['Admin', 'Trader', 'Analyst']} />} />
            <Route path="/decisions" element={<ProtectedRoute component={<DecisionLog />} roles={['Admin', 'Trader', 'Analyst']} />} />
            <Route path="/monitoring/transactions" element={<ProtectedRoute component={<TradeTransactionMonitor />} roles={['Admin', 'Trader', 'Analyst']} />} />
            <Route path="/admin/bugs" element={<ProtectedRoute component={<AdminBugs />} roles={['Admin']} />} />
            <Route path="/admin" element={<ProtectedRoute component={<Admin />} roles={['Admin']} />} />

            <Route path="/settings" element={<ProtectedRoute component={<UnifiedSettings />} roles={['Admin', 'Trader']} />} />
            <Route path="/universe" element={<ProtectedRoute component={<UniverseMonitor />} roles={['Admin', 'Trader']} />} />
            <Route path="/health" element={<ProtectedRoute component={<PlatformHealth />} roles={['Admin', 'Trader']} />} />
            <Route path="/hermes" element={<ProtectedRoute component={<HermesDashboard />} roles={['Admin']} />} />

            {/* Default and catch-all routes */}
            <Route
              path="/"
              element={<Navigate to={equitiesOnly ? '/trade/stocks/overview' : '/portfolio'} replace />}
            />
            <Route
              path="*"
              element={<Navigate to={equitiesOnly ? '/trade/stocks/overview' : '/portfolio'} replace />}
            />
          </Routes>
          </EquitiesRouteGuard>
        </main>
        <MobileTradeDock />
      </div>
      <BugReportWidget />
      <AskTitanPanel />
    </div>
    </div>
  );
};

function App() {

  return (
    <ErrorBoundary>
    <AuthProvider>
      <ThemeProvider>
        <TooltipProvider>
          <TradingViewModalProvider>
            <Router>
              <Routes>
                {/* Auth route - standalone full-screen, no sidebar */}
                <Route path="/login" element={<Login />} />
                {/* All routes with sidebar layout */}
                <Route path="*" element={<AppShell />} />
              </Routes>
            </Router>
          </TradingViewModalProvider>
        </TooltipProvider>
      </ThemeProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
